/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import type { ParamDict, ParamDictProps, ParamList } from "@utils/params";
import { Noise } from "@chi/wbax";
import {
  NonZero,
  type SynthDef,
  SynthOpts,
  SynthPreset,
  type SynthVoiceProps,
} from "@/synth/synth.tsx";
import {
  FadeEnv,
  HatRatios,
  MultiOsc,
  OscTypes,
  PercDecayEnv,
  SharpDecayEnv,
} from "@/synth/blocks.tsx";

//#region kick
export const Kick808Params = {
  tone: { type: "float", value: 167.1, min: 0, max: 500, step: 0.1 },
  decay: { type: "float", value: 0.5, min: 0, max: 2, step: 0.01 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
} satisfies ParamDict;
export type Kick808Opts = ParamDictProps<typeof Kick808Params>;

export const Kick808 = (props: Partial<Kick808Opts> & SynthVoiceProps) => {
  const { tone, decay, volume } = SynthOpts(Kick808Synth, props);
  const velocity = props.velocity ?? 1;
  const v = NonZero(volume * velocity);
  return (
    <gain
      gain={[
        { set: velocity, at: 0 },
        { linear: v, at: 0.1 },
        { exp: 0.01 * v, at: decay },
        { linear: 0, at: decay + 0.1 },
      ]}
    >
      <osc
        frequency={[{ set: tone, at: 0 }, { exp: 1, at: decay }]}
        stop={decay + 0.1}
        onEnded={props.onEnded}
      />
    </gain>
  );
};

export const Kick808Synth: SynthDef<typeof Kick808Params> = {
  label: "kick",
  params: Kick808Params,
  Component: Kick808,
  duration: (o) => o.decay + 0.1,
};
//#endregion

//#region snare
export const Snare808Params = {
  tone: { type: "float", value: 100, min: 0, max: 500, step: 0.1 },
  decay: { type: "float", value: 0.2, min: 0, max: 2, step: 0.01 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
} satisfies ParamDict;
export type Snare808Opts = ParamDictProps<typeof Snare808Params>;

export const Snare808 = (props: Partial<Snare808Opts> & SynthVoiceProps) => {
  const { tone, decay, volume } = SynthOpts(Snare808Synth, props);
  const velocity = props.velocity ?? 1;
  const v = NonZero(volume * velocity);
  return (
    <>
      <gain gain={[{ set: v, at: 0 }, { exp: 0.01, at: decay }]}>
        <biquad type="highpass" frequency={1000}>
          <Noise type="white" duration={decay} stop={decay} onEnded={props.onEnded} />
        </biquad>
      </gain>
      <gain gain={[{ set: 0.7 * v, at: 0 }, { exp: 0.01 * v, at: decay / 2 }]}>
        <osc type="triangle" frequency={tone} stop={decay} />
      </gain>
    </>
  );
};

export const Snare808Synth: SynthDef<typeof Snare808Params> = {
  label: "snare",
  params: Snare808Params,
  Component: Snare808,
  duration: (o) => o.decay,
};
//#endregion

//#region hihat
export const HiHat808Params = {
  tone: { type: "float", value: 130.81, min: 0, max: 500, step: 0.1 },
  decay: { type: "float", value: 0.5, min: 0, max: 2, step: 0.01 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  "oscs/rampUp": { type: "float", value: 0.05, min: 0.001, max: 1, step: 0.001 },
  "bandpass/frequency": { type: "int", value: 20000, min: 20, max: 22050 },
  "bandpass/q": { type: "float", value: 0.2, min: 0.1, max: 10, step: 0.1 },
  "highpass/frequency": { type: "int", value: 5000, min: 20, max: 22050 },
  "env/mid": { type: "float", value: 0.3, min: 0, max: 2, step: 0.01 },
  "env/attackRatio": { type: "float", value: 0.067, min: 0.001, max: 0.5, step: 0.001 },
  "env/midRatio": { type: "float", value: 0.1, min: 0.001, max: 0.9, step: 0.001 },
} satisfies ParamDict;
export type HiHat808Opts = ParamDictProps<typeof HiHat808Params>;

export const HiHat808 = (props: Partial<HiHat808Opts> & SynthVoiceProps) => {
  const o = SynthOpts(HiHat808Synth, props);
  const decay = o.decay;
  return (
    <gain
      gain={PercDecayEnv({
        volume: o.volume * (props.velocity ?? 1),
        decay,
        mid: o["env/mid"],
        attackRatio: o["env/attackRatio"],
        midRatio: o["env/midRatio"],
      })}
    >
      <biquad type="highpass" frequency={o["highpass/frequency"]}>
        <biquad type="bandpass" frequency={o["bandpass/frequency"]} Q={o["bandpass/q"]}>
          <gain gain={FadeEnv(0.8, o["oscs/rampUp"], 0, decay)}>
            <MultiOsc
              type="square"
              frequency={o.tone}
              ratios={HatRatios}
              stop={decay}
              onEnded={props.onEnded}
            />
          </gain>
        </biquad>
      </biquad>
    </gain>
  );
};

export const HiHat808Synth: SynthDef<typeof HiHat808Params> = {
  label: "hat",
  params: HiHat808Params,
  Component: HiHat808,
  duration: (o) => o.decay,
};

export const OpenHiHat808Synth = SynthPreset(HiHat808Synth, "open hat", {
  decay: 0.6,
  "bandpass/frequency": 10000,
  "bandpass/q": 2,
  "highpass/frequency": 3000,
  "env/mid": 0.5,
  "env/attackRatio": 0.05,
  "env/midRatio": 0.15,
});
//#endregion

//#region clap
export const Clap808Params = {
  tone: { type: "float", value: 130, min: 0, max: 500, step: 0.1 },
  decay: { type: "float", value: 0.3, min: 0, max: 2, step: 0.01 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  pulseWidth: { type: "float", value: 0.025, min: 0, max: 1, step: 0.001 },
} satisfies ParamDict;
export type Clap808Opts = ParamDictProps<typeof Clap808Params>;

export const Clap808 = (props: Partial<Clap808Opts> & SynthVoiceProps) => {
  const { tone, decay, volume, pulseWidth } = SynthOpts(Clap808Synth, props);
  const velocity = props.velocity ?? 1;
  const v = NonZero(volume * velocity);
  const pw = pulseWidth;
  return (
    <gain
      gain={[
        { set: v, at: 0 },
        { exp: 0.1, at: pw },
        { set: v, at: pw },
        { exp: 0.1, at: 2 * pw },
        { set: v, at: 2 * pw },
        { exp: 0.001, at: decay },
      ]}
    >
      <biquad type="bandpass" frequency={tone * 2}>
        <Noise type="white" duration={decay} stop={decay} onEnded={props.onEnded} />
      </biquad>
    </gain>
  );
};

export const Clap808Synth: SynthDef<typeof Clap808Params> = {
  label: "clap",
  params: Clap808Params,
  Component: Clap808,
  duration: (o) => o.decay,
};
//#endregion

//#region tom
export const Tom808Params = {
  tone: { type: "float", value: 130, min: 20, max: 2000, step: 0.1 },
  endFreq: { type: "float", value: 26, min: 0.1, max: 200, step: 0.1 },
  decay: { type: "float", value: 0.25, min: 0.01, max: 4, step: 0.01 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  floor: { type: "float", value: 0.01, min: 0.0001, max: 0.1, step: 0.0001 },
  tail: { type: "float", value: 0.02, min: 0, max: 0.5, step: 0.001 },
} satisfies ParamDict;
export type Tom808Opts = ParamDictProps<typeof Tom808Params>;

export const Tom808 = (props: Partial<Tom808Opts> & SynthVoiceProps) => {
  const o = SynthOpts(Tom808Synth, props);
  const end = o.decay + o.tail;
  return (
    <gain
      gain={SharpDecayEnv({
        volume: o.volume * (props.velocity ?? 1),
        decay: o.decay,
        floor: o.floor,
        tail: o.tail,
      })}
    >
      <osc
        frequency={[{ set: o.tone, at: 0 }, { exp: NonZero(o.endFreq), at: o.decay }]}
        stop={end}
        onEnded={props.onEnded}
      />
    </gain>
  );
};

export const Tom808Synth: SynthDef<typeof Tom808Params> = {
  label: "tom",
  params: Tom808Params,
  Component: Tom808,
  duration: (o) => o.decay + o.tail,
};
//#endregion

//#region cowbell
export const Cowbell808Params = {
  "osc1/type": { type: "list", value: "square", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc1/frequency": { type: "float", value: 562, min: 100, max: 2000, step: 1 },
  "osc2/type": { type: "list", value: "square", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc2/frequency": { type: "float", value: 845, min: 100, max: 2000, step: 1 },
  "filter/frequency": { type: "int", value: 800, min: 100, max: 4000 },
  "filter/q": { type: "float", value: 8, min: 0.1, max: 20, step: 0.1 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.2, min: 0.05, max: 1, step: 0.01 },
} satisfies ParamDict;
export type Cowbell808Opts = ParamDictProps<typeof Cowbell808Params>;

export const Cowbell808 = (props: Partial<Cowbell808Opts> & SynthVoiceProps) => {
  const o = SynthOpts(Cowbell808Synth, props);
  const v = NonZero(o.volume * (props.velocity ?? 1));
  const end = o.decay + 0.01;
  return (
    <gain gain={[{ set: v, at: 0 }, { exp: 0.001 * v, at: o.decay }, { linear: 0, at: end }]}>
      <biquad type="bandpass" frequency={o["filter/frequency"]} Q={o["filter/q"]}>
        <osc
          type={o["osc1/type"]}
          frequency={o["osc1/frequency"]}
          stop={end}
          onEnded={props.onEnded}
        />
        <osc type={o["osc2/type"]} frequency={o["osc2/frequency"]} stop={end} />
      </biquad>
    </gain>
  );
};

export const Cowbell808Synth: SynthDef<typeof Cowbell808Params> = {
  label: "cowbell",
  params: Cowbell808Params,
  Component: Cowbell808,
  duration: (o) => o.decay + 0.01,
};
//#endregion

//#region rimshot
export const Rimshot808Params = {
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  "noise/highpass": { type: "int", value: 1500, min: 20, max: 10000 },
  "noise/decay": { type: "float", value: 0.05, min: 0.001, max: 0.5, step: 0.001 },
  "click/frequency": { type: "float", value: 800, min: 20, max: 4000, step: 1 },
  "click/volume": { type: "float", value: 0.8, min: 0, max: 2, step: 0.01 },
  "click/decay": { type: "float", value: 0.03, min: 0.001, max: 0.5, step: 0.001 },
} satisfies ParamDict;
export type Rimshot808Opts = ParamDictProps<typeof Rimshot808Params>;

export function Rimshot808Duration(o: Rimshot808Opts) {
  return Math.max(o["noise/decay"], o["click/decay"]);
}

export const Rimshot808 = (props: Partial<Rimshot808Opts> & SynthVoiceProps) => {
  const o = SynthOpts(Rimshot808Synth, props);
  const v = o.volume * (props.velocity ?? 1);
  const noiseEnd = o["noise/decay"];
  const clickEnd = o["click/decay"];
  return (
    <>
      <gain gain={SharpDecayEnv({ volume: v, decay: noiseEnd })}>
        <biquad type="highpass" frequency={o["noise/highpass"]}>
          <Noise
            type="white"
            duration={noiseEnd}
            stop={noiseEnd}
            onEnded={noiseEnd >= clickEnd ? props.onEnded : undefined}
          />
        </biquad>
      </gain>
      <gain gain={SharpDecayEnv({ volume: o["click/volume"] * v, decay: clickEnd })}>
        <osc
          frequency={o["click/frequency"]}
          stop={clickEnd}
          onEnded={noiseEnd >= clickEnd ? undefined : props.onEnded}
        />
      </gain>
    </>
  );
};

export const Rimshot808Synth: SynthDef<typeof Rimshot808Params> = {
  label: "rimshot",
  params: Rimshot808Params,
  Component: Rimshot808,
  duration: Rimshot808Duration,
};
//#endregion

export const Drums808 = {
  kick: Kick808Synth,
  snare: Snare808Synth,
  hat: HiHat808Synth,
  openhat: OpenHiHat808Synth,
  clap: Clap808Synth,
  tom: Tom808Synth,
  cowbell: Cowbell808Synth,
  rimshot: Rimshot808Synth,
} satisfies Record<string, SynthDef<any>>;
